<template>

  <div>

    <!-- Common error message -->
    <div v-if="error">
      <div class="alert alert-danger text-center">
        {{ error }}
      </div>
    </div>

    <div v-if="tasksLoading" class="text-center">
      <!-- Loading indicator -->
      <i class="fa fa-spinner fa-spin fa-3x"></i>
    </div>
    <div v-else>

      <!-- Task list -->
      <div v-if="tasks.length" class="table-responsive">

        <!-- Filters -->
        <div class="form-row m-b-xxs m-t-xxs">

          <!-- By task status -->
          <div class="col-auto my-1">
            <label>Status:</label>
            <select class="form-control" v-model="filters.status.value">
              <option v-for="option in taskStatusOptions" :value="option.value">{{ option.label }}</option>
            </select>
          </div>

          <!-- By task type/text -->
          <div class="col-auto my-1">
            <label>Task:</label>
            <select class="form-control" v-model="filters.task_type_id.value">
              <option v-for="option in taskTypeOptions" :value="option.value">{{ option.label }}</option>
            </select>
          </div>

          <!-- By assignee -->
          <div class="col-auto my-1">
            <label>Assignee:</label>
            <select class="form-control" v-model="filters.assignee_ids.value">
              <option v-for="option in assigneeOptions" :value="option.value">{{ option.label }}</option>
            </select>
          </div>

          <div class="col-auto my-1">
            <label>&nbsp;</label>
            <button class="btn btn-sm btn-default form-control" value="clear" @click="resetFilters()">Clear</button>
          </div>

          <div class="col-auto my-1" v-if="allowCreateTask">
            <label>&nbsp;</label>
            <button class="btn btn-sm btn-primary form-control" @click="$modal.show('addTaskModal');">
              <i class="fa fa-fw fa-plus"></i> Add
            </button>
          </div>
        </div>

        <hr class="m-t-sm m-b-sm">

        <table class="table table-bordered table-hover m-b-xs m-t-xs">

          <thead>
            <tr>
              <th><a href="#" @click.prevent="sortBy('due_timestamp')">Due Date<span :class="sortIconClass('due_timestamp')"></span></a></th>
              <th><a href="#" @click.prevent="sortBy('status')">Status<span :class="sortIconClass('status')"></span></a></th>
              <th><a href="#" @click.prevent="sortBy('task')">Task<span :class="sortIconClass('task')"></span></a></th>
              <th>Assignees</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(task, index) in filteredTasks" :class="task.completed ? 'bg-secondary text-white' : ''">

              <td class="narrow-cell">
                <button :class="'btn btn-block btn-sm' + (task.completed ? ' btn-info btn-inverse' : ' btn-outline ' + (task.expired ? 'btn-danger' : 'btn-info'))"
                        @click="$modal.show('taskView' + task.id);">{{ task.date.due_date }}</button>
              </td>

              <td>
                {{ task.status }}
              </td>

              <td>
                {{ task.task }}
              </td>

              <td class="p-0">
                <ul class="list-group border-0 m-0">
                  <li v-for="user in task.assignee_list" class="border-0 p-2 list-group-item d-flex justify-content-between align-items-center">
                    <a :href="user.link" target="_blank" :class="task.completed ? 'text-white' : (replaced(user) ? 'text-danger' : '')">{{ user.name }}</a>
                    <span v-if="user.type_label" class="badge badge-secondary badge-pill">{{ user.type_label }}</span>
                  </li>
                </ul>
              </td>

              <td class="narrow-cell">
                <div class="btn-group btn-group-sm">
                  <template v-if="task.completed">
                    <button class="btn btn-danger" v-if="allowDeleteTask" @click="confirmDeleteTask(task)"><i class="fa fa-remove"></i> Delete</button>
                  </template>

                  <template v-else>
                    <button class="btn btn-primary" v-if="allowCompleteTask" @click="confirmCompleteTask(task)"><i class="fa fa-check"></i> Complete</button>
                    <button class="btn btn-primary" v-else-if="!allowCompleteTask" disabled><i class="fa fa-check"></i> Complete</button>

                    <div v-if="allowDeleteTask || allowEditTask" class="btn-group btn-group-sm">
                      <button type="button" class="btn btn-primary dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <span class="caret"></span>
                      </button>
                      <div class="dropdown-menu">
                        <a class="dropdown-item" href="#" v-if="allowEditTask" @click.prevent="$modal.show('taskEdit' + task.id);"><i class="fa fa-pencil"></i> Edit</a>
                        <a class="dropdown-item" href="#" v-if="allowDeleteTask" @click.prevent="confirmDeleteTask(task)"><i class="fa fa-remove"></i> Delete</a>
                      </div>
                    </div>
                  </template>
                </div>
              </td>

            </tr>
          </tbody>

        </table>

        <div v-if="!filteredTasks.length">
          <div class="alert alert-warning text-center m-t-xs m-b-xs">
            No tasks found
          </div>
        </div>

        <!-- Task modals -->
        <div v-for="(task, index) in tasks">
          <task-view :modal-id="'taskView' + task.id" v-model="tasks[index]" :claim-users="claimUsers"></task-view>

          <task-form v-if="allowEditTask"
                     :modal-id="'taskEdit' + task.id"
                     title="Edit task"
                     :claim-id="claimId"
                     :task-id="task.id"
                     :initial-task="task"
                     :task-types="taskTypes"
                     :user-types="userTypes"
                     :claim-users="claimUsers"
          ></task-form>
        </div>

      </div>

      <div v-else>
        <div class="alert alert-info text-center m-t-xs m-b-xs">
          The claim has no tasks.
        </div>
      </div>

    </div>

  </div>

</template>

<script>
  import TaskView from './TaskView'
  import TaskForm from './TaskForm'

  export default {

    /**
     * Input properties
     */
    props: {

      // Claim id
      claimId: {
        type: Number,
        required: true
      },

      // Allow create tasks.
      allowCreateTask: {
        type: Boolean,
        default: false
      },

      // Allow edit tasks.
      allowEditTask: {
        type: Boolean,
        default: false
      },

      // Allow complete tasks.
      allowCompleteTask: {
        type: Boolean,
        default: false
      },

      // Allow delete tasks.
      allowDeleteTask: {
        type: Boolean,
        default: false
      },

      // Task type list keyed by id.
      taskTypes: {
        type: Object,
        default: () => {}
      },

      // User types.
      userTypes: {
        type: Object,
        default: () => {}
      },

      // Claim users by type.
      claimUsers: {
        type: Object,
        default: () => {}
      },
    },

    /**
     * Used components
     */
    components: {
      TaskView,
      TaskForm
    },

    /**
     * Variables (models).
     */
    data () {
      return {

        // Task list.
        tasks: [],

        // Default filters.
        defaultFilters: {
          task_type_id: {value: 'all', type: 'match'},
          status: {value: 'all', type: 'match'},
          assignee_ids: {value: 'all', type: 'in'},
        },

        // Current filters.
        filters: {},

        // Sort criteria: 'due_date', 'text'.
        sortedBy: 'due_date',

        // Sort direction: 'asc', 'desc', 'toggle'.
        sortedDirection: 'asc',

        // Common error message.
        error: '',

        // Task list loading indicator.
        tasksLoading: false,

      }
    },

    /**
     * Computed values.
     */
    computed: {
      // Task type filter options.
      taskTypeOptions() {
        let options = Object.entries(this.taskTypes).map((type) => {
          return {value: type[0], label: type[1]};
        });
        options.unshift({value: 'all', label: 'All'});
        options.push({value: null, label: 'Other'});
        return options;
      },

      // Task status filter options.
      taskStatusOptions() {
        return [
          {value: 'all', label: 'All'},
          {value: 'Active', label: 'Active'},
          {value: 'Completed', label: 'Completed'},
        ];
      },

      // Assignee filter options.
      assigneeOptions() {
        let options = [];
        options.push({value: 'all', label: 'All'});
        this.tasks.forEach(task => {
          task.assignee_list.forEach(user => options.push({value: user.id, label: user.name}));
        });
        // Leave only unique users.
        return options.reduce((unique, option)=>{
          let exist = unique.find(({value}) => option.value === value);
          if(!exist){
            unique.push(option);
          }
          return unique;
        }, []);
      },

      // Boolean filter options.
      booleanOptions() {
        return [
          {value: 'all', label: 'All'},
          {value: true, label: 'Yes'},
          {value: false, label: 'No'}
        ];
      },

      // Filtered tasks.
      filteredTasks() {
        let filteredItems = this.tasks;

        for (var filterName in this.filters) {
          if(this.filters.hasOwnProperty(filterName)) {

            let filterValue = this.filters[filterName].value;
            let filterType = this.filters[filterName].type;
            let filterAlsoSearch = this.filters[filterName].alsoSearch ? this.filters[filterName].alsoSearch : [];

            if (!((filterValue === 'all') || filterValue === '')) {
              filteredItems = filteredItems.filter(item => {

                switch (filterType) {
                  case 'contains':
                    let searchValues = [];
                    if (item[filterName]) searchValues.push(item[filterName]);
                    if (filterAlsoSearch.length) {
                      filterAlsoSearch.forEach((value) => {
                        if (item[value]) searchValues.push(item[value]);
                      });
                    }
                    if (searchValues.length) {
                      let found = false;
                      searchValues.forEach((searchValue) => {
                        let value = searchValue.toLowerCase();
                        let search = filterValue.toLowerCase();
                        if (value.indexOf(search) > -1) found = true;
                      });
                      return found;
                    }
                    break;

                  case 'in':
                    return item[filterName].indexOf(filterValue) > -1;

                  case 'match':
                  default:
                    if (item[filterName]) {
                      return item[filterName] == filterValue;
                    } else if (!filterValue) return true;
                    break;
                }

              });
            }
          }
        }
        return filteredItems;
      },
    },

    /**
     * Functions (methods).
     */
    methods: {

      /**
       * Init component.
       */
      init() {
        // Initial load tasks.
        this.loadTasks();
        this.resetFilters();
        this.filters.status.value = 'Active';
        this.sortBy('due_timestamp');
      },

      /**
       * Load tasks.
       */
      loadTasks() {
        this.error = '';
        this.tasksLoading = true;
        axios.get('/claims/' + this.claimId + '/tasks/list')
          .then(response =>{
            this.tasks = response.data;
            this.sortBy(this.sortedBy, this.sortedDirection);
          })
          .catch(error =>{
            this.handleErrors(error, 'Unable to load tasks.')
          })
          .then(() =>{
            this.tasksLoading = false;
          });
      },

      /**
       * Is claim role user replaced (e.g. adjuster).
       */
      replaced(user) {
        if (user.type === '') return false;
        if (this.claimUsers[user.type] !== undefined) {
          if (this.claimUsers[user.type].id !== user.id) {
            return true;
          }
        }
        return false;
      },

      /**
       * Complete task dialog.
       */
      confirmCompleteTask(task) {
        let text = 'You are about to complete the task. ';
        text += 'This action cannot be undone.';

        this.$swal.fire({
          title: 'Complete task',
          text: text,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Confirm'
        }).then((result) => {
          if (result.value) {
            this.completeTask(task);
          }
        })
      },

      /**
       * Complete task.
       */
      completeTask(task) {
        const formData = new FormData();
        formData.append('_method', "PATCH");
        axios.post('/claims/' + this.claimId + '/tasks/' + task.id + '/complete', formData)
          .then(response => {
            this.$swal.fire(
              'Completed',
              'The task is completed.',
              'success'
            );
            this.loadTasks();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to complete task')
          });
      },

      /**
       * Delete task dialog.
       */
      confirmDeleteTask(task) {
        let text = 'You are about to delete task. ';
        text += 'This action cannot be undone.';

        this.$swal.fire({
          title: 'Delete task',
          text: text,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Delete'
        }).then((result) => {
          if (result.value) {
            this.deleteTask(task);
          }
        })
      },

      /**
       * Delete task.
       */
      deleteTask(task) {
        const formData = new FormData();
        formData.append('_method', "DELETE");
        axios.post('/claims/' + this.claimId + '/tasks/' + task.id, formData)
          .then(response => {
            this.$swal.fire(
              'Deleted',
              'The task has been deleted.',
              'success'
            );
            this.loadTasks();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to delete task')
          });
      },

      /**
       * Sort tasks by specified column/direction.
       */
      sortBy(by, direction = 'toggle') {
        if (direction === 'toggle') {
          if (by === this.sortedBy) {
            direction = this.sortedDirection === 'asc' ? 'desc' : 'asc';
          } else direction = 'asc';
        }

        if (direction === 'asc') {
          this.tasks.sort((a, b) => ((a[by] ? a[by] : '') > (b[by] ? b[by] : '')) ? 1 : -1);
        } else {
          this.tasks.sort((a, b) => ((a[by] ? a[by] : '') > (b[by] ? b[by] : '')) ? -1 : 1);
        }

        this.sortedBy = by;
        this.sortedDirection = direction;
      },

      /**
       * Sort icon class to show near the column name.
       */
      sortIconClass(column) {
        return this.sortedBy === column ? 'fa fa-sort-' + this.sortedDirection : '';
      },

      /**
       * Reset filters.
       */
      resetFilters() {
        this.filters = _.cloneDeep(this.defaultFilters);
      },

      /**
       * Handle axios errors
       *
       * @param errorObj
       * @param defaultMessage
       */
      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 403) {
          this.error = defaultMessage + ': access denied.';
        } else {
          this.error = defaultMessage;
        }
      },

    },

    /**
     * Initialize component on mount.
     */
    mounted () {
      this.init();
    }

  }
</script>

<style scoped>
  .narrow-cell {
    width: 150px;
  }
  .btn-group {
    display: flex;
  }
  @media (max-width: 767px) {
    .table-responsive .dropdown-menu {
      position: static !important;
    }
  }
  @media (min-width: 768px) {
    .table-responsive {
      overflow: inherit;
    }
  }
</style>
